import { useEffect, useState, useRef, useMemo, useCallback } from "react"
import type { MouseEvent as ReactMouseEvent } from "react"
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels"
import { LeftSidebar } from "./left-sidebar"
import { DocumentViewer } from "./document-viewer"
import { RightChatPanel } from "./right-chat-panel"
import { CommandPalette } from "./command-palette"
import { Header } from "./header"
import { ProductTourHost } from "./product-tour"
import { startProductTour } from "@/lib/product-tour"
import { SettingsDialog } from "../features/settings-dialog"
import { KeyboardShortcutsDialog } from "../ui/keyboard-shortcuts-dialog"
import { GripVertical } from "lucide-react"
import { useUrlSync, markTabClickNavigation } from "@/hooks/use-url-sync"
import {
  ChatSurfaceRegistryProvider,
  chatConversationTabId,
} from "@/hooks/use-chat-surface-registry"
import type { OpenIntentClickEvent } from "@/lib/open-intent"
import { isChatConversationTabId } from "./document-viewer.utils"
import { ChatSurfaceRegistryBridge } from "@/components/features/chats/ChatSurfaceRegistryBridge"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { openDocumentInNewWindow, cleanupWindowState } from "@/lib/window-manager"
import { getExplorerTabTitle, type OpenTab } from "../features/document-tabs"
import {
  FLOW_OPEN_SURFACE_EVENT,
  type FlowStudioSurface,
} from "@/components/features/flows/flow-studio-events"
import type { ChatReference } from "./resizable-layout.types"
import type { ViewContext } from "./chat-pane.types"
import {
  chatReferenceFromOpenTab,
  dispatchFolderAddToChat,
  expandableShellPanelPercents,
  expandedChatPanelPercent,
  expandedLeftPanelPercent,
  getDocumentTypeFromPath,
} from "./resizable-layout.utils"
import {
  ShellChromeProvider,
  ShellCollapsedAgentEdge,
  ShellCollapsedRail,
  ShellHostSlot,
  ShellMainDismiss,
} from "./resizable-layout.flyout"
import {
  DRIVE_FOCUS_WORKSPACE_EVENT,
  OPEN_DRIVE_PREVIEW_EVENT,
  type OpenDrivePreviewDetail,
} from "@/components/integrations/google-workspace/drive-browse-events"
import {
  OPEN_PLATFORM_SKILL_EVENT,
  type OpenPlatformSkillDetail,
} from "@/lib/open-platform-skill"
import { bindDriveIndexTabToWorkspace } from "@/components/integrations/google-workspace/drive-focus-workspace"
import {
  NEXTCLOUD_OPEN_EVENT,
  type NextcloudOpenDetail,
} from "@/components/integrations/nextcloud/nextcloud-browse-events"
import {
  useResizableLayoutState,
  useTabMigration,
  useExplorerNavigationInit,
  useListTabNavigationInit,
  useWindowCleanup,
  useAutoOpenHomeTab,
  useBrowserTabTitleSync,
} from "./resizable-layout.hooks"
import { useShellColumnPanels } from "./resizable-layout.panels"
import {
  useTabHandlers,
  useNavigationHandlers,
  useExplorerHandlers,
  useUrlHandlers,
  useEventListeners,
  resolveWorkspaceForDocument,
} from "./resizable-layout.handlers"
import { isActiveTabDocumentInChatScope, resolveTabWorkspaceId } from "./resizable-layout.handlers/shared"
import { useWorkspace } from "@/providers/workspace-provider"
import { useContentRevisionPoll } from "@/hooks/use-content-revision-poll"
import { useStructureRevisionSync } from "@/hooks/use-structure-revision-sync"
import { testIds } from "@/lib/test-ids"
import { useHorizontalWheelTabNavigation } from "./resizable-layout.wheel-tab-nav"
import { useResizableLayoutKeyboardShortcuts } from "./resizable-layout.keyboard-shortcuts"
import {
  useSyncActiveDocumentPathForTree,
  useSyncSelectedFolderFromExplorerTab,
  useSyncFeedbackUiContext,
  useInitExplorerNavIfEmpty,
  useRecoverOrphanOrHiddenActiveTab,
  useClearActiveDocumentIdWhenNotExplorer,
  useBrowseFolderHighlightEvent,
  useShowDocumentInBrowseAndOpenDocumentEvents,
  useDocumentPathUpdatedEvent,
  usePinDocumentByPathEvent,
  usePinTabByIdEvent,
  useFlownotesFocusViewEvent,
  useFlownotesToggleLeftSidebarEvent,
  useChatAgentPanelCollapseEvent,
  useFolderDeletedTabCleanup,
} from "./resizable-layout.tab-sync"
import { useWorkspaceOnlyUndoToast } from "@/hooks/use-workspace-only-undo-toast"

import { fetchWithAuth } from "@/lib/api"
import {
  InternalMarkdownLinkProvider,
} from "@/lib/internal-link-navigation"
import type { SidebarDocumentDragPayload } from "@/lib/sidebar-document-drag"
import { extractOpenFilePathsForSearch } from "@/lib/command-palette-ranking"
import { useDocumentSearchBoostIds } from "@/hooks/use-document-search-boost-ids"
import {
  panelDestinationPreviewClass,
  usePanelDestinationPreview,
} from "@/lib/panel-destination-preview"
import { cn } from "@/lib/utils"
import { NavigationDebugPanel } from "../features/navigation-debug-panel"
import { navDebug } from "@/lib/nav-debug"

function useNavDebugVisibility() {
  const [enabled, setEnabled] = useState(() => navDebug.isPanelEnabled())
  useEffect(() => {
    return navDebug.subscribe(() => {
      setEnabled(navDebug.isPanelEnabled())
    })
  }, [])
  return enabled
}

export function ResizableLayout() {
  const navigate = useNavigate()
  useContentRevisionPoll()
  const navDebugVisible = useNavDebugVisibility()

  const [browseExplorerRefreshToken, setBrowseExplorerRefreshToken] = useState(0)
  const bumpBrowseExplorer = useCallback(() => {
    setBrowseExplorerRefreshToken((k) => k + 1)
  }, [])
  useEffect(() => {
    const bump = () => bumpBrowseExplorer()
    // Do not listen to `flownotes-content-revision` for browse refresh: that event
    // fires on any content DB change (MCP edits, autosave, external file sync).
    // The explorer API only reflects paths/folders; body changes do not alter the
    // tree. Refetching on every revision caused full-tree reload and visible flash.
    // Structural changes are covered by document-* / folder-renamed events and
    // cross-client `flownotes-structure-revision` (poll from GET /api/content-revision).
    window.addEventListener("document-created", bump)
    window.addEventListener("document-deleted", bump)
    window.addEventListener("document-renamed", bump)
    window.addEventListener("folder-renamed", bump)
    window.addEventListener("document-moved", bump)
    window.addEventListener("folder-created", bump)
    window.addEventListener("folder-deleted", bump)
    window.addEventListener("flownotes-structure-revision", bump)
    return () => {
      window.removeEventListener("document-created", bump)
      window.removeEventListener("document-deleted", bump)
      window.removeEventListener("document-renamed", bump)
      window.removeEventListener("folder-renamed", bump)
      window.removeEventListener("document-moved", bump)
      window.removeEventListener("folder-created", bump)
      window.removeEventListener("folder-deleted", bump)
      window.removeEventListener("flownotes-structure-revision", bump)
    }
  }, [bumpBrowseExplorer])

  // Get all state from custom hook
  const state = useResizableLayoutState()

  // Destructure state for easier access
  const {
    windowId,
    showSidebar,
    setShowSidebar,
    sidebarCollapsed,
    setSidebarCollapsed,
    showAgent,
    setShowAgent,
    agentCollapsed,
    setAgentCollapsed,
    showDocumentList,
    setShowDocumentList,
    openTabs,
    setOpenTabs,
    activeTabId,
    setActiveTabId,
    tabBarActiveId,
    setTabBarActiveId,
    selectedProjectId,
    setSelectedProjectId,
    closedTabsHistory,
    setClosedTabsHistory,
    panelSizes,
    expandedLeftSize,
    expandedRightSize,
    commandPaletteOpen,
    setCommandPaletteOpen,
    settingsOpen,
    setSettingsOpen,
    keyboardShortcutsOpen,
    setKeyboardShortcutsOpen,
    isFullscreen,
    setIsFullscreen,
    chatReferences,
    setChatReferences,
    activeDocumentPath,
    setActiveDocumentPath,
    activeDocumentIdForFolderView,
    setActiveDocumentIdForFolderView,
    navigation,
  } = state

  const panelDestinationPreview = usePanelDestinationPreview()
  const {
    chatPanelRef,
    sidebarPanelRef,
    shellRowRef,
    toggleSidebarCollapsed,
    persistChatExpandedWidthIfExpanded,
    setAgentCollapsedPreservingLeftPx,
    expandAgentPanel,
    expandLeftSidebar,
    toggleAgentCollapsed,
    toggleSidebarAutoHide,
    dockLeftFromRail,
    dockAgentFromRail,
    onShellPanelLayout,
    onShellDragPointerDown,
    flyoutOpen,
    setFlyoutOpen,
    sidebarAutoHide,
    leftDockDisabledReason,
    agentDockDisabledReason,
    roomForLeft,
    roomForAgent,
  } = useShellColumnPanels(state)

  const { openTabIds: searchBoostOpenTabIds, recentlyViewedIds: searchBoostRecentlyViewedIds } =
    useDocumentSearchBoostIds(openTabs)
  const paletteOpenTabFilePaths = useMemo(
    () => extractOpenFilePathsForSearch(openTabs),
    [openTabs],
  )

  // Use extracted hooks
  useTabMigration(openTabs, setOpenTabs, activeTabId, setActiveTabId)
  useListTabNavigationInit(navigation)
  useExplorerNavigationInit(activeTabId, openTabs, selectedProjectId, navigation)

  // Track triage / Activity view context so the chat pane can see the table contents
  const [triageViewContext, setTriageViewContext] = useState<ViewContext | null>(null)
  const [activityViewContext, setActivityViewContext] = useState<ViewContext | null>(null)
  const [productBoardViewContext, setProductBoardViewContext] = useState<ViewContext | null>(null)
  useEffect(() => {
    const handleTriageUpdate = (e: Event) => {
      const { scope, items, loadedAt } = (e as CustomEvent).detail
      setTriageViewContext({ type: 'triage', scope, items, loadedAt })
    }
    window.addEventListener('triage-context-updated', handleTriageUpdate)
    return () => window.removeEventListener('triage-context-updated', handleTriageUpdate)
  }, [])
  useEffect(() => {
    const handleActivityUpdate = (e: Event) => {
      const { who, kind, linkedOnly, projectId, items, loadedAt } = (e as CustomEvent).detail ?? {}
      setActivityViewContext({
        type: 'basecamp-activity',
        who: who === 'mine' || who === 'team' ? who : 'org',
        kind: kind === 'comments' || kind === 'completions' || kind === 'updates' ? kind : 'all',
        ...(linkedOnly ? { linkedOnly: true } : {}),
        ...(projectId ? { projectId: String(projectId) } : {}),
        items: Array.isArray(items) ? items : [],
        loadedAt: loadedAt || new Date().toISOString(),
      })
    }
    window.addEventListener('basecamp-activity-context-updated', handleActivityUpdate)
    return () => window.removeEventListener('basecamp-activity-context-updated', handleActivityUpdate)
  }, [])
  useEffect(() => {
    const handleProductBoardUpdate = (e: Event) => {
      const { view, openBetTitle, openBetPath } = (e as CustomEvent).detail ?? {}
      setProductBoardViewContext({ type: 'product_board', view, openBetTitle, openBetPath })
    }
    window.addEventListener('product-board-view-context', handleProductBoardUpdate)
    return () => window.removeEventListener('product-board-view-context', handleProductBoardUpdate)
  }, [])
  useWindowCleanup(windowId)

  const { workspaces, activeWorkspaces, activeWorkspaceIds, toggleWorkspace, setActiveWorkspaceIds, activeFolderPaths } = useWorkspace()

  // Reconcile Basecamp integration tabs whose workspaceId was assigned from the fallback
  // org before workspaces loaded. Once the org list arrives, patch any tab whose
  // integrationData.workspaceSlug now resolves to a real workspace id.
  const reconciledWorkspacesRef = useRef(false)
  useEffect(() => {
    if (workspaces.length === 0) return
    if (reconciledWorkspacesRef.current) return
    reconciledWorkspacesRef.current = true

    setOpenTabs((prev) => {
      let changed = false
      const next = prev.map((tab) => {
        const slug = tab.integrationData?.workspaceSlug
        if (!slug) return tab
        const resolvedId = workspaces.find((w) => w.slug === slug)?.id
        if (!resolvedId) return tab
        if (tab.workspaceId === resolvedId) return tab
        changed = true
        return {
          ...tab,
          workspaceId: resolvedId,
          integrationData: tab.integrationData
            ? { ...tab.integrationData, organizationId: resolvedId }
            : tab.integrationData,
        }
      })
      return changed ? next : prev
    })
  }, [workspaces, setOpenTabs])

  // Use all workspaces so explorer tabs keep a stable owning workspaceId even when that workspace is toggled off (tabs then hide via visibility helper).
  const getWorkspaceIdForPath = useCallback(
    (path: string) => resolveWorkspaceForDocument(path, workspaces),
    [workspaces]
  )
  const workspacesRoute = useMemo(
    () => workspaces.map((w) => ({ id: w.id, slug: w.slug, contentRoot: w.contentRoot })),
    [workspaces],
  )

  // Resolve workspace color for folder icon tinting in the explorer
  const explorerWorkspaceColor = useMemo(() => {
    if (!selectedProjectId) return undefined
    const wsId = resolveWorkspaceForDocument(selectedProjectId, activeWorkspaces)
    if (!wsId) return undefined
    const ws = activeWorkspaces.find(w => w.id === wsId)
    return ws?.color ?? undefined
  }, [selectedProjectId, activeWorkspaces])

  // Get handlers from hooks (needed for URL tab restore)
  const tabHandlers = useTabHandlers(
    openTabs,
    setOpenTabs,
    activeTabId,
    setActiveTabId,
    closedTabsHistory,
    setClosedTabsHistory,
    setActiveDocumentPath,
    navigation,
    navigate,
    getWorkspaceIdForPath,
    activeWorkspaceIds,
    setActiveWorkspaceIds
  )

  const navigationHandlers = useNavigationHandlers(
    openTabs,
    setOpenTabs,
    activeTabId,
    setActiveTabId,
    setSelectedProjectId,
    setActiveDocumentPath,
    navigation,
    navigate,
    getWorkspaceIdForPath,
    workspacesRoute,
  )

  const explorerHandlers = useExplorerHandlers(
    openTabs,
    setOpenTabs,
    activeTabId,
    setActiveTabId,
    setSelectedProjectId,
    setShowDocumentList,
    navigation,
    navigate,
    getWorkspaceIdForPath,
    workspacesRoute,
  )

  const urlHandlers = useUrlHandlers(
    openTabs,
    setOpenTabs,
    activeTabId,
    setActiveTabId,
    setSelectedProjectId,
    navigation,
    tabHandlers.createNewTab,
    tabHandlers.switchToExistingTab,
    getWorkspaceIdForPath
  )

  useAutoOpenHomeTab(openTabs, activeTabId, setOpenTabs, setActiveTabId, navigation, navigate)
  useBrowserTabTitleSync(openTabs, activeTabId, workspacesRoute)

  const [settingsDefaultTab, setSettingsDefaultTab] = useState<string | null>(null)
  const [settingsFocusOrganizationId, setSettingsFocusOrganizationId] = useState<string | null>(null)
  const [settingsFocusSection, setSettingsFocusSection] = useState<string | null>(null)
  useEffect(() => {
    const handler = (e: CustomEvent<{
      tab?: string
      organizationId?: string
      focusSection?: string
      section?: string
    }>) => {
      setSettingsOpen(true)
      const rawTab = e.detail?.tab ?? null
      const tab =
        rawTab === 'organization' || rawTab === 'workspaces' ? 'organizations' : rawTab
      setSettingsDefaultTab(tab)
      setSettingsFocusOrganizationId(e.detail?.organizationId ?? null)
      setSettingsFocusSection(e.detail?.focusSection || e.detail?.section || null)
    }
    window.addEventListener('open-settings', handler as EventListener)
    return () => window.removeEventListener('open-settings', handler as EventListener)
  }, [])

  // Use event listeners
  useEventListeners(
    openTabs,
    setOpenTabs,
    activeTabId,
    setActiveTabId,
    setActiveDocumentPath,
    showSidebar,
    setShowSidebar,
    sidebarCollapsed,
    setSidebarCollapsed,
    navigation,
    tabHandlers.handleDocumentClick
  )

  // Destructure handlers for easier access
  const {
    handleDocumentClick,
    handleDocumentClose,
    handleCloseManyDocuments,
    handleReopenLastClosedTab,
    handleDocumentReorder,
    openDrivePreviewTab,
    openPlatformSkillTab,
  } = tabHandlers

  const {
    handleGoBack,
    handleGoForward,
  } = navigationHandlers

  const {
    handleProjectClick,
    handleHomeClick,
    handleInboxClick,
    handleDriveClick,
    handleNextcloudOpen,
    handleOpenInboxThreadMain,
    handleOpenChatConversationMain,
    handleShowFileLocation,
    handleWorkspaceHomeClick,
    handleDeskHomeClick,
    handleFlowIndexClick,
  } = explorerHandlers

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<NextcloudOpenDetail>).detail
      if (!detail?.path) return
      handleNextcloudOpen(detail)
    }
    window.addEventListener(NEXTCLOUD_OPEN_EVENT, handler)
    return () => window.removeEventListener(NEXTCLOUD_OPEN_EVENT, handler)
  }, [handleNextcloudOpen])

  useEffect(() => {
    const handler = (event: Event) => {
      const filePath = (event as CustomEvent<{ filePath?: string }>).detail?.filePath
      if (!filePath) return
      handleShowFileLocation(filePath)
    }
    window.addEventListener('show-file-location', handler)
    return () => window.removeEventListener('show-file-location', handler)
  }, [handleShowFileLocation])

  const handleNativeSidebarExplorerDropOnTabBar = useCallback(
    (payload: SidebarDocumentDragPayload) => {
      if (payload.nodeType === 'folder') {
        handleProjectClick(payload.path)
      } else {
        const openInNewTabHint = {
          metaKey: true,
          ctrlKey: false,
          preventDefault: () => {},
          stopPropagation: () => {},
        } as unknown as ReactMouseEvent
        void handleDocumentClick(payload.path, openInNewTabHint)
      }
    },
    [handleProjectClick, handleDocumentClick]
  )

  // Stable callbacks for ChatPane (avoid re-renders when ResizableLayout re-renders)
  const onRemoveReference = useCallback((id: string, ref?: ChatReference) => {
    if (ref) {
      setChatReferences(prev =>
        prev.filter(r => {
          if (r.id !== ref.id) return true
          return r.lineStart !== ref.lineStart || r.lineEnd !== ref.lineEnd
        })
      )
    } else {
      setChatReferences(prev => prev.filter(r => r.id !== id))
    }
  }, [setChatReferences])
  const onClearReferences = useCallback(() => setChatReferences([]), [setChatReferences])
  const onAddReference = useCallback((ref: ChatReference) => {
    setChatReferences(prev => {
      const isDuplicate = prev.some(r => {
        if (r.id !== ref.id) return false
        return r.lineStart === ref.lineStart && r.lineEnd === ref.lineEnd
      })
      if (!isDuplicate) return [...prev, ref]
      return prev
    })
  }, [setChatReferences])

  // Listen for open-workspace-home events dispatched from the sidebar
  useEffect(() => {
    const handler = (e: Event) => {
      const { workspaceId } = (e as CustomEvent).detail ?? {}
      if (!workspaceId) return
      const ws = activeWorkspaces.find(w => w.id === workspaceId)
      handleWorkspaceHomeClick(workspaceId, ws?.name ?? workspaceId)
    }
    window.addEventListener('open-workspace-home', handler)
    return () => window.removeEventListener('open-workspace-home', handler)
  }, [handleWorkspaceHomeClick, activeWorkspaces])

  useEffect(() => {
    const handler = () => handleInboxClick()
    window.addEventListener('open-inbox-main', handler)
    return () => window.removeEventListener('open-inbox-main', handler)
  }, [handleInboxClick])

  useEffect(() => {
    const handler = (event: Event) => {
      const surface = (event as CustomEvent<{ surface?: FlowStudioSurface }>).detail?.surface
      if (!surface) return
      handleFlowIndexClick(surface)
    }
    window.addEventListener(FLOW_OPEN_SURFACE_EVENT, handler)
    return () => window.removeEventListener(FLOW_OPEN_SURFACE_EVENT, handler)
  }, [handleFlowIndexClick])

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<OpenDrivePreviewDetail>).detail
      if (!detail?.file?.id || !detail.organizationId) return
      openDrivePreviewTab(detail.file, {
        organizationId: detail.organizationId,
        event: detail.event,
        importedDocumentId: detail.importedDocumentId,
      })
    }
    window.addEventListener(OPEN_DRIVE_PREVIEW_EVENT, handler)
    return () => window.removeEventListener(OPEN_DRIVE_PREVIEW_EVENT, handler)
  }, [openDrivePreviewTab])

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<OpenPlatformSkillDetail>).detail
      if (!detail?.slug) return
      openPlatformSkillTab(detail)
    }
    window.addEventListener(OPEN_PLATFORM_SKILL_EVENT, handler)
    return () => window.removeEventListener(OPEN_PLATFORM_SKILL_EVENT, handler)
  }, [openPlatformSkillTab])

  useEffect(() => {
    const handler = (e: Event) => {
      const workspaceId = (e as CustomEvent<{ workspaceId?: string }>).detail?.workspaceId
      if (!workspaceId) return
      setOpenTabs((prev) => bindDriveIndexTabToWorkspace(prev, workspaceId))
    }
    window.addEventListener(DRIVE_FOCUS_WORKSPACE_EVENT, handler)
    return () => window.removeEventListener(DRIVE_FOCUS_WORKSPACE_EVENT, handler)
  }, [setOpenTabs])

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{
        workspaceId?: string
        deskPath?: string
        deskSlug?: string
      }>).detail ?? {}
      if (!detail.workspaceId) return
      const ws = activeWorkspaces.find(w => w.id === detail.workspaceId)
      handleDeskHomeClick(
        detail.workspaceId,
        ws?.name ?? detail.workspaceId,
        detail.deskPath ?? '',
        detail.deskSlug ?? '',
      )
    }
    window.addEventListener('open-desk-home', handler as EventListener)
    return () => window.removeEventListener('open-desk-home', handler as EventListener)
  }, [handleDeskHomeClick, activeWorkspaces])

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ threadId?: string; title?: string; workspaceId?: string; clickEvent?: OpenIntentClickEvent }>).detail ?? {}
      if (!detail.threadId) return
      handleOpenInboxThreadMain(detail.threadId, detail.title, detail.workspaceId, detail.clickEvent)
    }
    window.addEventListener('open-inbox-thread-main', handler as EventListener)
    return () => window.removeEventListener('open-inbox-thread-main', handler as EventListener)
  }, [handleOpenInboxThreadMain])
  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<{ threadId?: string; title?: string; openInNewTab?: boolean; clickEvent?: OpenIntentClickEvent }>).detail ?? {}
      if (!detail.threadId) return
      handleOpenChatConversationMain(detail.threadId, detail.title, {
        openInNewTab: detail.openInNewTab,
        clickEvent: detail.clickEvent,
      })
    }
    const onFocus = (e: Event) => {
      const detail = (e as CustomEvent<{ threadId?: string }>).detail ?? {}
      if (!detail.threadId) return
      const tabId = chatConversationTabId(detail.threadId)
      if (openTabs.some((tab) => tab.id === tabId)) {
        setActiveTabId(tabId)
        markTabClickNavigation()
      }
    }
    const onClose = (e: Event) => {
      const detail = (e as CustomEvent<{ threadId?: string }>).detail ?? {}
      if (!detail.threadId) return
      handleDocumentClose(chatConversationTabId(detail.threadId))
    }
    const onTogglePin = (e: Event) => {
      const detail = (e as CustomEvent<{ threadId?: string }>).detail ?? {}
      if (!detail.threadId) return
      const tabId = chatConversationTabId(detail.threadId)
      setOpenTabs((prev) =>
        prev.map((tab) =>
          tab.id === tabId ? { ...tab, isPinned: !tab.isPinned } : tab,
        ),
      )
    }
    const onRename = (e: Event) => {
      const detail = (e as CustomEvent<{ threadId?: string; title?: string }>).detail ?? {}
      if (!detail.threadId || !detail.title) return
      const tabId = chatConversationTabId(detail.threadId)
      setOpenTabs((prev) =>
        prev.map((tab) =>
          tab.id === tabId ? { ...tab, title: detail.title!.slice(0, 80) } : tab,
        ),
      )
    }
    const onMigrate = (e: Event) => {
      const detail = (e as CustomEvent<{ fromThreadId?: string; toThreadId?: string; title?: string }>).detail ?? {}
      if (!detail.fromThreadId || !detail.toThreadId) return
      const fromTabId = chatConversationTabId(detail.fromThreadId)
      const toTabId = chatConversationTabId(detail.toThreadId)
      const rawTitle = (detail.title?.trim() || "New chat").slice(0, 80)
      setOpenTabs((prev) =>
        prev.map((tab) =>
          tab.id === fromTabId
            ? {
                ...tab,
                id: toTabId,
                title: rawTitle,
                type: "chat-conversation" as const,
                chatThreadId: detail.toThreadId,
              }
            : tab,
        ),
      )
      if (activeTabId === fromTabId) {
        setActiveTabId(toTabId)
        markTabClickNavigation()
      }
    }

    window.addEventListener("open-chat-conversation-main", onOpen as EventListener)
    window.addEventListener("focus-chat-conversation-main", onFocus as EventListener)
    window.addEventListener("close-chat-conversation-tab", onClose as EventListener)
    window.addEventListener("toggle-pin-chat-conversation-tab", onTogglePin as EventListener)
    window.addEventListener("rename-chat-conversation-tab", onRename as EventListener)
    window.addEventListener("migrate-chat-conversation-tab", onMigrate as EventListener)
    return () => {
      window.removeEventListener("open-chat-conversation-main", onOpen as EventListener)
      window.removeEventListener("focus-chat-conversation-main", onFocus as EventListener)
      window.removeEventListener("close-chat-conversation-tab", onClose as EventListener)
      window.removeEventListener("toggle-pin-chat-conversation-tab", onTogglePin as EventListener)
      window.removeEventListener("rename-chat-conversation-tab", onRename as EventListener)
      window.removeEventListener("migrate-chat-conversation-tab", onMigrate as EventListener)
    }
  }, [
    handleOpenChatConversationMain,
    handleDocumentClose,
    setOpenTabs,
    setActiveTabId,
    openTabs,
    activeTabId,
  ])


  // URL synchronization - handles URL-based navigation (bookmarks, direct links, browser back/forward)
  const handleUrlChange = urlHandlers.handleUrlChange

  // Initialize URL sync (with safeguards against jumping)
  useUrlSync({
    activeTabId,
    openTabs,
    onUrlChange: handleUrlChange,
  });

  // Helper: Switch to next/previous tab
  const switchToNextTab = useCallback(() => {
    const currentIndex = openTabs.findIndex(tab => tab.id === activeTabId)
    if (currentIndex < openTabs.length - 1) {
      const nextTab = openTabs[currentIndex + 1]
      setActiveTabId(nextTab.id)
    }
  }, [openTabs, activeTabId, setActiveTabId])

  const switchToPreviousTab = useCallback(() => {
    const currentIndex = openTabs.findIndex(tab => tab.id === activeTabId)
    if (currentIndex > 0) {
      const prevTab = openTabs[currentIndex - 1]
      setActiveTabId(prevTab.id)
    }
  }, [openTabs, activeTabId, setActiveTabId])

  useHorizontalWheelTabNavigation(switchToNextTab, switchToPreviousTab)

  useSyncActiveDocumentPathForTree(activeTabId, openTabs, setActiveDocumentPath)
  useSyncSelectedFolderFromExplorerTab(activeTabId, openTabs, setSelectedProjectId)
  useSyncFeedbackUiContext(activeTabId, openTabs, activeDocumentPath)
  useInitExplorerNavIfEmpty(activeTabId, openTabs, selectedProjectId, navigation)
  useRecoverOrphanOrHiddenActiveTab(
    openTabs,
    activeTabId,
    setActiveTabId,
    workspaces,
    activeWorkspaceIds,
    setOpenTabs,
  )
  useWorkspaceOnlyUndoToast(
    workspaces,
    activeWorkspaceIds,
    setActiveWorkspaceIds,
    openTabs,
    activeTabId,
    setActiveTabId,
  )
  useClearActiveDocumentIdWhenNotExplorer(activeTabId, openTabs, setActiveDocumentIdForFolderView)
  useBrowseFolderHighlightEvent(activeTabId, openTabs, selectedProjectId)
  useShowDocumentInBrowseAndOpenDocumentEvents(
    showSidebar,
    sidebarCollapsed,
    setShowSidebar,
    setSidebarCollapsed,
    handleDocumentClick
  )
  useDocumentPathUpdatedEvent(
    openTabs,
    activeTabId,
    setOpenTabs,
    setActiveTabId,
    setActiveDocumentPath,
    navigation
  )
  useStructureRevisionSync({
    openTabs,
    activeTabId,
    setOpenTabs,
    setActiveTabId,
    setActiveDocumentPath,
    navigate,
    onBrowseRefresh: bumpBrowseExplorer,
  })
  usePinDocumentByPathEvent(setOpenTabs)
  usePinTabByIdEvent(setOpenTabs)
  useFlownotesFocusViewEvent(
    setSidebarCollapsed,
    setAgentCollapsed,
    persistChatExpandedWidthIfExpanded,
  )
  useChatAgentPanelCollapseEvent(setAgentCollapsedPreservingLeftPx, persistChatExpandedWidthIfExpanded)
  useFlownotesToggleLeftSidebarEvent(toggleSidebarCollapsed)
  useFolderDeletedTabCleanup(
    openTabs,
    activeTabId,
    setOpenTabs,
    setActiveTabId,
    selectedProjectId,
    setSelectedProjectId,
    navigate,
    workspacesRoute,
  )

  const handleDocumentDragToChat = (tabId: string, title: string, type: OpenTab["type"]) => {
    if (agentCollapsed) setAgentCollapsedPreservingLeftPx(false)
    const tab = openTabs.find(t => t.id === tabId)
    if (!tab) return

    if (type === "explorer" || tab.type === "explorer") {
      const folderPath = tab.explorerData?.folderPath?.trim()
      const displayTitle = getExplorerTabTitle(tab, workspacesRoute) || title
      if (!folderPath) return
      dispatchFolderAddToChat(folderPath, displayTitle)
      return
    }

    const displayTitle = title
    const newReference = chatReferenceFromOpenTab(tab, displayTitle)
    if (!newReference) return
    if (chatReferences.some((ref) => ref.id === newReference.id)) return
    setChatReferences([...chatReferences, newReference])
  }

  const handleTabAddToChat = (tabId: string) => {
    const tab = openTabs.find(t => t.id === tabId)
    if (!tab) return
    const title =
      tab.type === "explorer" ? getExplorerTabTitle(tab, workspacesRoute) : tab.title
    handleDocumentDragToChat(tabId, title, tab.type)
  }

  // Handle drag to chat from tree view (files/folders)
  const handleTreeDragToChat = (nodeId: string, title: string, path: string, type: 'file' | 'folder') => {
    if (agentCollapsed) setAgentCollapsedPreservingLeftPx(false)

    if (type === 'folder') {
      dispatchFolderAddToChat(path || nodeId, title)
      return
    }

    if (chatReferences.find(ref => ref.id === nodeId)) return

    const newReference = {
      id: nodeId,
      title: title,
      type: getDocumentTypeFromPath(path),
      path,
    }

    setChatReferences([...chatReferences, newReference])
  }

  // Handle "Add to Chat" from context menu
  // Helper to get file path for a tab
  const getTabFilePath = async (tabId: string): Promise<string | null> => {
    // If tabId is already a path, return it
    if (tabId.includes('/')) {
      return tabId
    }

    // Skip special tabs
    if (tabId === 'home' || tabId === 'list' || tabId === 'basecamp' || tabId === 'graph' ||
        tabId === 'edit-history' || tabId === 'view:edit-history' || tabId === 'search-view' ||
        tabId === 'people-index' ||
        tabId.startsWith('person-view-') ||
        tabId.startsWith('new-') || tabId.startsWith('basecamp-') || tabId.startsWith('integration-') ||
        tabId.startsWith('workspace-home-')) {
      return null
    }

    if (tabId.startsWith('inbox-thread-') || isChatConversationTabId(tabId)) {
      return null
    }

    try {
      const response = await fetchWithAuth(`/api/documents/${tabId}`, {
        credentials: 'include'
      })
      if (response.ok) {
        const docData = await response.json()
        return docData.path || docData.filePath || docData.file_path || docData.relative_path || null
      }
    } catch (err) {
      console.error('[getTabFilePath] Failed to fetch:', err)
    }

    return null
  }

  // Tab context menu handlers
  const handleTabShowFileLocation = (tabId: string) => {
    // Switch to the tab first, then show location
    const tab = openTabs.find(t => t.id === tabId)
    if (tab) {
      setActiveTabId(tabId)
      // Use existing handler
      getTabFilePath(tabId).then(path => {
        if (path) {
          handleShowFileLocation(path)
        }
      })
    }
  }

  const handleTabRename = (tabId: string) => {
    // Switch to tab and trigger rename
    const tab = openTabs.find(t => t.id === tabId)
    if (tab) {
      setActiveTabId(tabId)
      // Dispatch event to trigger rename dialog in DocumentViewer
      window.dispatchEvent(new CustomEvent('trigger-rename', { detail: { tabId } }))
    }
  }

  const handleTabMove = (tabId: string) => {
    // Switch to tab and trigger move
    const tab = openTabs.find(t => t.id === tabId)
    if (tab) {
      setActiveTabId(tabId)
      // Dispatch event to trigger move dialog in DocumentViewer
      window.dispatchEvent(new CustomEvent('trigger-move', { detail: { tabId } }))
    }
  }

  const handleTabExportPDF = (tabId: string) => {
    // Switch to tab and trigger PDF export
    const tab = openTabs.find(t => t.id === tabId)
    if (tab) {
      setActiveTabId(tabId)
      // Dispatch event to trigger PDF export in DocumentViewer
      window.dispatchEvent(new CustomEvent('trigger-export-pdf', { detail: { tabId } }))
    }
  }

  const handleTabCloseOthers = (tabId: string) => {
    // Keep only the specified tab
    const tabToKeep = openTabs.find(t => t.id === tabId)
    const newTabs: OpenTab[] = []
    if (tabToKeep) {
      newTabs.push(tabToKeep)
    }

    setOpenTabs(newTabs)
    setActiveTabId(tabId)
  }

  const handleTabMoveToWindow = (tabId: string) => {
    const tab = openTabs.find(t => t.id === tabId)
    if (!tab) {
      toast.error('Tab not found')
      return
    }

    // Open in new window
    const newWindow = openDocumentInNewWindow(tabId, 'move', openTabs, activeTabId || 'list')

    if (!newWindow) {
      toast.error('Failed to open new window', {
        description: 'Please allow popups for this site'
      })
      return
    }

    // Close tab in current window
    const updatedTabs = openTabs.filter(t => t.id !== tabId)
    setOpenTabs(updatedTabs)

    // If it was active, switch to another tab
    if (activeTabId === tabId) {
      const nextTab = updatedTabs.find(t => t.id !== 'list') || updatedTabs[0]
      setActiveTabId(nextTab?.id || 'list')
    }

    toast.success('Moved to new window', {
      description: tab.title
    })
  }

  const handleTabCopyToWindow = (tabId: string) => {
    const tab = openTabs.find(t => t.id === tabId)
    if (!tab) {
      toast.error('Tab not found')
      return
    }

    // Open in new window (keeps tab in current window)
    const newWindow = openDocumentInNewWindow(tabId, 'copy', openTabs, activeTabId || 'list')

    if (!newWindow) {
      toast.error('Failed to open new window', {
        description: 'Please allow popups for this site'
      })
      return
    }

    toast.success('Copied to new window', {
      description: tab.title
    })
  }

  const handleTabPinToggle = useCallback(
    (tabId: string) => {
      setOpenTabs((prev) =>
        prev.map((tab) =>
          tab.id === tabId ? { ...tab, isPinned: !tab.isPinned } : tab,
        ),
      )
    },
    [setOpenTabs],
  )

  /** Shared with Open Tabs sidebar rows (ADR-0020). Bookmark toggle stays in DocumentViewer. */
  const tabMenuHandlers = useMemo(
    () => ({
      onTabPinToggle: handleTabPinToggle,
      onTabClose: handleDocumentClose,
      onTabCloseOthers: handleTabCloseOthers,
      onTabShowFileLocation: handleTabShowFileLocation,
      onTabRename: handleTabRename,
      onTabMove: handleTabMove,
      onTabExportPDF: handleTabExportPDF,
      onTabAddToChat: handleTabAddToChat,
      onTabMoveToWindow: handleTabMoveToWindow,
      onTabCopyToWindow: handleTabCopyToWindow,
      getTabFilePath,
    }),
    // Handlers above close over openTabs/activeTabId; rebind when those change.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional broad deps for tab menu stability
    [
      handleTabPinToggle,
      handleDocumentClose,
      openTabs,
      activeTabId,
    ],
  )

  // Handle window close - cleanup state
  useEffect(() => {
    if (!windowId) return

    const handleBeforeUnload = () => {
      cleanupWindowState(windowId)
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [windowId])

  // Listen for messages from other windows (for move mode)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return

      if (event.data.type === 'TAB_MOVED') {
        // Another window moved a tab - close it here if it exists
        const movedTabId = event.data.tabId
        const tabExists = openTabs.some(t => t.id === movedTabId)

        if (tabExists) {
          const updatedTabs = openTabs.filter(t => t.id !== movedTabId)
          setOpenTabs(updatedTabs)

          if (activeTabId === movedTabId) {
            const nextTab = updatedTabs.find(t => t.id !== 'list') || updatedTabs[0]
            setActiveTabId(nextTab?.id || 'list')
          }
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [openTabs, activeTabId, setOpenTabs, setActiveTabId])

  const handleAddToChat = (item: { type: "file" | "folder"; id: string; path: string; title?: string }) => {
    if (agentCollapsed) {
      setAgentCollapsedPreservingLeftPx(false)
    }

    if (item.type === 'folder') {
      dispatchFolderAddToChat(item.path || item.id, item.title)
      return
    }

    if (chatReferences.find(ref => ref.id === item.id)) return

    const newReference = {
      id: item.id,
      title: item.title || item.path.split('/').pop() || item.id,
      type: getDocumentTypeFromPath(item.path),
      path: item.path,
    }

    setChatReferences([...chatReferences, newReference])
  }

  const handleSendToChat = (text: string, documentId: string) => {
    // Add the text as a new document reference in chat
    const newReference = {
      id: documentId,
      title: "New Document Draft",
      type: "note" as const,  // Default to 'note' as per schema
    }

    setChatReferences([...chatReferences, newReference])

    // TODO: Also send the text as a message to chat
    // This would require updating the chat pane to accept messages programmatically
  }

  const handleDocumentListToggle = () => {
    setShowDocumentList(!showDocumentList)
  }

  // All handlers are now provided by extracted hooks above
  // Removed duplicate implementations: handleGoBack, fetchDocumentData, createNewTab, replaceCurrentTab, switchToExistingTab, handleGoForward

  // Removed duplicate handleGoForward - now using navigationHandlers.handleGoForward

  // Removed duplicate trackDocumentView and handleDocumentClick - now using tabHandlers
  // Tab path, explorer init, recovery, browse/scroll, document-path, pin, focus-view: resizable-layout.tab-sync.ts (hooks run above, after trackpad nav)

  useResizableLayoutKeyboardShortcuts({
    handleGoBack,
    handleGoForward,
    switchToPreviousTab,
    switchToNextTab,
    activeTabId,
    handleDocumentClose,
    handleReopenLastClosedTab,
    openTabs,
    setOpenTabs,
    sidebarCollapsed,
    toggleSidebarCollapsed,
    toggleAgentCollapsed,
    setCommandPaletteOpen,
    setKeyboardShortcutsOpen,
  })

  const handleFullscreenToggle = () => {
    const nextFullscreen = !isFullscreen
    setIsFullscreen(nextFullscreen)
    if (nextFullscreen) {
      persistChatExpandedWidthIfExpanded()
      // Hide sidebar and agent pane when entering fullscreen
      setShowSidebar(false)
      setShowAgent(false)
    } else {
      // Restore sidebar and agent pane when exiting fullscreen
      setShowSidebar(true)
      setShowAgent(true)
    }
  }

  const getActiveTabBasePath = useCallback(() => {
    const tab = openTabs.find((t) => t.id === activeTabId)
    return (
      tab?.filePath ??
      (activeTabId && activeTabId.includes("/") ? activeTabId : undefined)
    )
  }, [openTabs, activeTabId])

  const sidebarInGroup = Boolean(showSidebar && !sidebarCollapsed && !isFullscreen)
  const agentInGroup = Boolean(showAgent && !agentCollapsed && !isFullscreen)
  const showCollapsedSidebarRail = Boolean(showSidebar && sidebarCollapsed && !isFullscreen)
  const showCollapsedAgentRail = Boolean(showAgent && agentCollapsed && !isFullscreen)
  const shellPercents = expandableShellPanelPercents(
    expandedLeftPanelPercent(panelSizes, expandedLeftSize),
    expandedChatPanelPercent(panelSizes, expandedRightSize),
    { sidebarInGroup, agentInGroup },
  )
  const sidebarInner = (
    <LeftSidebar
      openTabs={openTabs}
      activeTabId={activeTabId}
      activeDocumentPath={activeDocumentPath}
      browseExplorerRefreshToken={browseExplorerRefreshToken}
      onDocumentClick={handleDocumentClick}
      onDocumentClose={handleDocumentClose}
      onCloseAllOpenDocuments={() => {
        setOpenTabs([])
        setActiveTabId(null)
      }}
      onCloseManyDocuments={handleCloseManyDocuments}
      onDocumentReorder={handleDocumentReorder}
      onDocumentListToggle={handleDocumentListToggle}
      onProjectClick={handleProjectClick}
      showDocumentList={showDocumentList}
      collapsed={sidebarCollapsed}
      onToggleCollapse={toggleSidebarCollapsed}
      selectedProjectId={selectedProjectId}
      onDragToChat={handleTreeDragToChat}
      onTabDragToChat={handleDocumentDragToChat}
      onAddToChat={handleAddToChat}
      onHomeClick={handleHomeClick}
      onOpenDrivePreview={openDrivePreviewTab}
      onInboxClick={handleInboxClick}
      onDriveClick={handleDriveClick}
      tabMenuHandlers={tabMenuHandlers}
    />
  )

  return (
    <ChatSurfaceRegistryProvider>
    <ChatSurfaceRegistryBridge />
    <InternalMarkdownLinkProvider
      navigateToDocument={handleDocumentClick}
      getActiveTabBasePath={getActiveTabBasePath}
    >
    <div className="h-full w-full flex flex-col min-h-0" data-testid={testIds.resizableLayout.container}>
      {!isFullscreen && (
        <Header
          onSearchClick={() => setCommandPaletteOpen(true)}
          onKeyboardShortcutsClick={() => setKeyboardShortcutsOpen(true)}
          onTakeTourClick={() => startProductTour()}
          className="print-hide"
        />
      )}
      <ProductTourHost
        ensureLeftSidebarVisible={expandLeftSidebar}
        ensureAgentPanelVisible={expandAgentPanel}
      />
      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
        onDocumentSelect={handleDocumentClick}
        openTabDocumentIds={searchBoostOpenTabIds}
        recentlyViewedDocumentIds={searchBoostRecentlyViewedIds}
        openTabFilePaths={paletteOpenTabFilePaths}
        folderPaths={activeFolderPaths.length > 0 ? activeFolderPaths : undefined}
        workspaces={workspaces}
        activeWorkspaceIds={activeWorkspaceIds}
        selectedProjectId={selectedProjectId}
        openTabs={openTabs}
        activeTabId={activeTabId}
      />
      <SettingsDialog
        open={settingsOpen}
        onOpenChange={(open) => {
          setSettingsOpen(open)
          if (!open) {
            setSettingsDefaultTab(null)
            setSettingsFocusOrganizationId(null)
            setSettingsFocusSection(null)
          }
        }}
        defaultTab={settingsDefaultTab}
        focusOrganizationId={settingsFocusOrganizationId}
        focusSection={settingsFocusSection}
      />
      <KeyboardShortcutsDialog
        open={keyboardShortcutsOpen}
        onOpenChange={setKeyboardShortcutsOpen}
      />

      <ShellChromeProvider
        autoHide={sidebarAutoHide}
        onToggleAutoHide={toggleSidebarAutoHide}
        leftDisabledReason={leftDockDisabledReason}
        agentDisabledReason={agentDockDisabledReason}
        flyoutOpen={flyoutOpen}
        onFlyoutOpenChange={setFlyoutOpen}
        sidebar={sidebarInner}
      >
      <div ref={shellRowRef} className="flex flex-1 min-h-0 min-w-0 print-hide">
        {showCollapsedSidebarRail ? (
          <ShellCollapsedRail roomForDock={roomForLeft} onDock={dockLeftFromRail}>
            <ShellHostSlot className="h-full min-h-0" />
          </ShellCollapsedRail>
        ) : null}
      <PanelGroup
        direction="horizontal"
        className="min-h-0 min-w-0 flex-1"
        onLayout={onShellPanelLayout}
      >
        {sidebarInGroup ? (
          <>
            <Panel
              ref={sidebarPanelRef}
              id="flownotes-sidebar-panel"
              order={1}
              defaultSize={shellPercents.left ?? 20}
              minSize={4}
              maxSize={30}
            >
              <ShellHostSlot className="h-full min-h-0 flex flex-col" testId={testIds.resizableLayout.sidebar} />
            </Panel>
            <PanelResizeHandle
              className="w-px bg-border hover:bg-primary transition-colors relative group"
              onPointerDown={() => onShellDragPointerDown()}
            >
              <div className="absolute inset-y-0 -left-1 -right-1 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </div>
            </PanelResizeHandle>
          </>
        ) : null}

        {/* Center Document Viewer */}
        <Panel
          id="flownotes-main-panel"
          order={2}
          defaultSize={shellPercents.main}
          minSize={30}
        >
          <ShellMainDismiss
            className={cn(
              'relative h-full flex flex-col overflow-hidden panel-hover-glow',
              panelDestinationPreviewClass(panelDestinationPreview, 'main'),
            )}
          >
            <DocumentViewer
              onFullscreenToggle={handleFullscreenToggle}
              isFullscreen={isFullscreen}
              onDocumentDragToChat={handleDocumentDragToChat}
              onSendToChat={handleSendToChat}
              openTabs={openTabs}
              setOpenTabs={setOpenTabs}
              activeTabId={activeTabId || 'list'}
              setActiveTabId={setActiveTabId}
              tabBarActiveId={tabBarActiveId}
              setTabBarActiveId={setTabBarActiveId}
              showDocumentList={showDocumentList}
              onDocumentListToggle={handleDocumentListToggle}
              selectedProjectId={selectedProjectId}
              workspaceColor={explorerWorkspaceColor}
              navigation={navigation}
              onGoBack={handleGoBack}
              onGoForward={handleGoForward}
              onDocumentClick={handleDocumentClick}
              onShowFileLocation={handleShowFileLocation}
              onFolderClick={handleProjectClick}
              activeDocumentIdForFolderView={activeDocumentIdForFolderView}
              onTabShowFileLocation={handleTabShowFileLocation}
              onTabRename={handleTabRename}
              onTabMove={handleTabMove}
              onTabExportPDF={handleTabExportPDF}
              onTabAddToChat={handleTabAddToChat}
              onTabCloseOthers={handleTabCloseOthers}
              onTabMoveToWindow={handleTabMoveToWindow}
              onTabCopyToWindow={handleTabCopyToWindow}
              getTabFilePath={getTabFilePath}
              onUrlChange={handleUrlChange}
              onNativeSidebarExplorerDropOnTabBar={handleNativeSidebarExplorerDropOnTabBar}
              setSelectedProjectId={setSelectedProjectId}
              setShowDocumentList={setShowDocumentList}
              getWorkspaceIdForPath={getWorkspaceIdForPath}
            />
          </ShellMainDismiss>
        </Panel>

        {agentInGroup ? (
          <PanelResizeHandle
            className="w-px bg-border hover:bg-primary transition-colors relative group"
            onPointerDown={() => onShellDragPointerDown()}
          >
            <div className="absolute inset-y-0 -left-1 -right-1 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
          </PanelResizeHandle>
        ) : null}
        {agentInGroup ? (
          <Panel
            ref={chatPanelRef}
            id="flownotes-chat-panel"
            order={3}
            defaultSize={shellPercents.chat ?? 32}
            minSize={4}
            maxSize={80}
            className="min-w-0 overflow-hidden"
          >
            <div
              className={cn(
                'h-full min-h-0 flex flex-col panel-hover-glow',
                panelDestinationPreviewClass(panelDestinationPreview, 'agent'),
              )}
              data-testid={testIds.resizableLayout.chatPane}
            >
            <RightChatPanel
              onAgentPanelHost={expandAgentPanel}
              activeDocumentId={(() => {
                const currentTab = openTabs.find(tab => tab.id === activeTabId)
                if (!currentTab || currentTab.type === 'explorer' || !activeTabId) return undefined
                if (!isActiveTabDocumentInChatScope(currentTab, workspaces, activeWorkspaceIds)) {
                  return undefined
                }
                return activeTabId
              })()}
              activeDocumentTitle={(() => {
                const currentTab = openTabs.find(tab => tab.id === activeTabId)
                if (!currentTab || currentTab.type === 'explorer') return undefined
                if (!isActiveTabDocumentInChatScope(currentTab, workspaces, activeWorkspaceIds)) {
                  return undefined
                }
                return currentTab.title || undefined
              })()}
              activeDocumentPath={(() => {
                const currentTab = openTabs.find(tab => tab.id === activeTabId)
                if (!currentTab || currentTab.type === 'explorer') return undefined
                if (!isActiveTabDocumentInChatScope(currentTab, workspaces, activeWorkspaceIds)) {
                  return undefined
                }
                const path =
                  currentTab.filePath ||
                  (typeof currentTab.id === 'string' && currentTab.id.includes('/')
                    ? currentTab.id
                    : undefined)
                return path || undefined
              })()}
              viewContext={(() => {
                if (activeTabId === 'basecamp-triage' || activeTabId === 'basecamp-todos') return triageViewContext ?? undefined
                const currentTab = openTabs.find(tab => tab.id === activeTabId)
                const isActivityTab =
                  activeTabId === 'basecamp-activity' ||
                  (currentTab?.type === 'integration-basecamp' &&
                    (currentTab?.integrationData?.section === 'activity' ||
                      currentTab?.id === 'basecamp-activity'))
                if (isActivityTab) {
                  return (
                    activityViewContext ?? {
                      type: 'basecamp-activity' as const,
                      items: [],
                      who: 'org' as const,
                      kind: 'all' as const,
                      loadedAt: '',
                    }
                  )
                }
                if (currentTab?.type === 'integration-product-board') {
                  return productBoardViewContext ?? { type: 'product_board' as const }
                }
                const folderPath = currentTab?.type === 'explorer'
                  ? (currentTab.explorerData?.folderPath ?? selectedProjectId ?? null)
                  : null
                if (folderPath) return { type: 'folder' as const, folderPath }
                if (
                  currentTab?.type === 'integration-basecamp-todo' ||
                  (typeof activeTabId === 'string' &&
                    /^basecamp-todo-\d/.test(activeTabId))
                ) {
                  const todoId =
                    currentTab?.integrationData?.todoId ||
                    (typeof activeTabId === 'string'
                      ? activeTabId.replace(/^basecamp-todo-/, '')
                      : '')
                  const projectId = currentTab?.integrationData?.projectId
                  if (todoId && projectId) {
                    return {
                      type: 'basecamp-todo' as const,
                      todoId: String(todoId),
                      projectId: String(projectId),
                      title: (currentTab?.title || '').trim() || 'Basecamp todo',
                    }
                  }
                }
                if (activeTabId?.startsWith('basecamp-project-')) {
                  const projectId =
                    currentTab?.integrationData?.projectId ??
                    activeTabId.replace(/^basecamp-project-/, '')
                  const projectName =
                    (currentTab?.integrationData?.projectName ?? currentTab?.title ?? '').trim() ||
                    'Basecamp project'
                  if (projectId) {
                    const organizationId =
                      currentTab?.integrationData?.organizationId ??
                      (typeof currentTab?.workspaceId === 'string' ? currentTab.workspaceId : undefined)
                    return {
                      type: 'basecamp-project' as const,
                      projectId,
                      projectName,
                      ...(organizationId ? { organizationId } : {}),
                    }
                  }
                }
                return undefined
              })()}
              references={chatReferences}
              onRemoveReference={onRemoveReference}
              onClearReferences={onClearReferences}
              onAddReference={onAddReference}
              collapsed={agentCollapsed}
              onToggleCollapse={toggleAgentCollapsed}
              onDocumentClick={handleDocumentClick}
              onFolderClick={handleProjectClick}
              openTabIds={searchBoostOpenTabIds}
              recentlyViewedIds={searchBoostRecentlyViewedIds}
            />
            </div>
          </Panel>
        ) : null}
      </PanelGroup>
        {showCollapsedAgentRail ? (
          <ShellCollapsedAgentEdge roomForDock={roomForAgent} onDock={dockAgentFromRail}>
            <div
              className={cn(
                "flex h-full min-h-0 flex-col panel-hover-glow",
                panelDestinationPreviewClass(panelDestinationPreview, "agent"),
              )}
              data-testid={testIds.resizableLayout.chatPane}
            >
              <RightChatPanel
                onAgentPanelHost={expandAgentPanel}
                collapsed
                onToggleCollapse={toggleAgentCollapsed}
                onDocumentClick={handleDocumentClick}
                onFolderClick={handleProjectClick}
                references={chatReferences}
                onRemoveReference={onRemoveReference}
                onClearReferences={onClearReferences}
                onAddReference={onAddReference}
                openTabIds={searchBoostOpenTabIds}
                recentlyViewedIds={searchBoostRecentlyViewedIds}
              />
            </div>
          </ShellCollapsedAgentEdge>
        ) : null}
      </div>
      </ShellChromeProvider>
      {navDebugVisible && (
        <NavigationDebugPanel
          navigation={navigation}
          openTabs={openTabs}
          activeTabId={activeTabId}
        />
      )}
    </div>
    </InternalMarkdownLinkProvider>
    </ChatSurfaceRegistryProvider>
  )
}
